import "./QuestionCard.scss";

import { Alert, Button, Flex, Segment, Text } from "@fluentui/react-northstar";
import { FieldDescriptors } from "@shopify/react-form-state";
import {
  createNominalQuestion,
  createOpenEndedQuestion,
  createOrdinalQuestion,
  randomString,
} from "@teams/client-shared";
import {
  NominalQuestion,
  OpenEndedQuestion,
  OrdinalQuestion,
  Question,
  QuestionTemplate,
  QuestionType,
} from "@teams/types";
import { QuestionTemplateDTO } from "@teams/types/api";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";

import CoreLogo from "../../../../../_shared/assets/polly-logo-no-text.svg";
import { PollyDialog } from "../../../../../_shared/components/PollyDialog";
import { PollyQuestionEdit } from "../../PollyQuestionEdit";
import { PollyQuestionTypeMenu } from "../../PollyQuestionTypeMenu";
import { PollyQuestionTypeMenuInMeeting } from "../../PollyQuestionTypeMenuInMeeting";
import {
  AuthoringFields,
  NominalQuestionWithChoices,
  QuestionTypeSelectionMenuTypes,
} from "../types";
import { CollapsedQuestion } from "./CollapsedQuestion";
import { QuestionCardMenu } from "./QuestionCardMenu";

type Props = {
  index: number;
  fields: FieldDescriptors<AuthoringFields>;
  questionField: FieldDescriptors<Question<QuestionType>>;
  compressed?: boolean;
  questionTemplates: QuestionTemplate[];
  isEdit?: boolean;
  isLimitedEdit?: boolean;
  originalQuestion?: Question<QuestionType>;
  addQuestion: (
    fields: FieldDescriptors<AuthoringFields>,
    question: Question<QuestionType>
  ) => void;
  showHighLight: boolean;
  collapsed?: boolean;
  setCollapsedForQuestion: (
    questionId: string,
    collapsed: boolean,
    fields: FieldDescriptors<AuthoringFields>
  ) => void;
  expandAllQuestions: () => void;
  isMeetingSidePanel?: boolean;
};

export const QuestionCard: React.FC<Props> = ({
  fields,
  index,
  questionField,
  compressed,
  questionTemplates,
  isEdit,
  isLimitedEdit,
  originalQuestion,
  addQuestion,
  showHighLight,
  collapsed,
  setCollapsedForQuestion,
  expandAllQuestions,
  isMeetingSidePanel,
}) => {
  const [nominalQuestionChoices, setNominalQuestionChoices] = useState<
    Array<NominalQuestionWithChoices>
  >([]);
  const [showTypeChangeDialog, setShowTypeChangeDialog] = useState(false);
  const [newQuestionType, setNewQuestionType] = useState<{
    type: QuestionTypeSelectionMenuTypes;
    templateId?: string;
  }>();

  const questionSettings: {
    [key: string]: {
      allowUserAddOptions?: boolean;
      allowMultipleChoices?: boolean;
      allowComments?: boolean;
      commentAppeal?: string;
    };
  } = useMemo(() => ({}), []);
  const ref = useRef(null);

  const originalQuestionChoices =
    originalQuestion?.questionType !== "open-ended"
      ? (originalQuestion as NominalQuestion)?.choices.map(c => c.id) || []
      : [];

  const questionValue = useMemo(() => {
    const questionIndex = fields.questions.value.findIndex(q => q.id === questionField.id.value);
    return fields.questions.value[questionIndex];
  }, [fields.questions.value, questionField.id.value]);

  // Set errors if any on the FieldDescriptor instance so that they can be displayed
  if (questionValue.validationErrors) {
    questionField.label.error = questionValue.validationErrors.labelError;
    if (questionValue.validationErrors.choicesError) {
      (questionField as FieldDescriptors<NominalQuestion>).choices.error =
        questionValue.validationErrors.choicesError;
    }
  }

  const convertFormType = useCallback(
    (newType: { type: QuestionTypeSelectionMenuTypes; templateId?: string }) => {
      const existingQuestionId = questionField.id.value;
      if (fields.type.value === "quiz") {
        // Convert to multi choice
        const updatedQuestions = fields.questions.value.map(question => {
          if (question.id === existingQuestionId) {
            switch (newType?.type) {
              case "nominal":
                (question as NominalQuestion).choices.forEach(choice => {
                  delete choice.correct;
                });
                break;
              case "ordinal":
                const ordinal = createOrdinalQuestion(
                  {
                    label: question.label,
                    allowComments: !!question.allowComments,
                    commentAppeal: question.commentAppeal,
                    required: !!question.required,
                  },
                  undefined,
                  newType?.templateId
                    ? questionTemplates.find(t => t.id === newType?.templateId)
                    : undefined
                );
                question = ordinal;
                question.id = existingQuestionId;
                break;
              case "open-ended":
                question = createOpenEndedQuestion({
                  label: question.label,
                  required: question.required,
                });
                question.id = existingQuestionId;
                break;
              default:
                break;
            }
          } else if (question.questionType === "nominal") {
            (question as NominalQuestion).choices.forEach(choice => {
              delete choice.correct;
            });
          }
          return question;
        });

        fields.questions.onChange(updatedQuestions);

        if (updatedQuestions.length > 1) {
          fields.type.onChange("survey");
        } else {
          fields.type.onChange("poll");
        }
      } else {
        // Convert to quiz
        const updatedQuestions = fields.questions.value.map(question => {
          switch (question.questionType) {
            case "nominal":
              (question as NominalQuestion).choices.forEach((choice, index) => {
                choice.correct = index === 0;
              });
              break;

            case "ordinal":
              const ordinalQuestion = question as OrdinalQuestion;
              question = createNominalQuestion({
                label: ordinalQuestion.label,
                choices: ordinalQuestion.choices.map((choice, index) => ({
                  id: randomString(),
                  correct: index === 0,
                  value: choice.label,
                })),
                allowComments: !!ordinalQuestion.allowComments,
                commentAppeal: ordinalQuestion.commentAppeal,
                required: ordinalQuestion.required,
              });
              question.id = ordinalQuestion.id;
              break;

            case "open-ended":
              const openEndedQuestion = question as OpenEndedQuestion;
              question = createNominalQuestion(
                {
                  label: openEndedQuestion.label,
                  required: openEndedQuestion.required,
                },
                undefined,
                undefined,
                true
              );
              question.id = openEndedQuestion.id;
              break;
          }
          return question;
        });

        expandAllQuestions();

        if (fields.resultsVisibility.value === "real-time") {
          fields.resultsVisibility.onChange("after-close");
        }

        fields.questions.onChange(updatedQuestions);
        fields.type.onChange("quiz");
      }
    },
    [
      expandAllQuestions,
      fields.questions,
      fields.resultsVisibility,
      fields.type,
      questionField.id.value,
      questionTemplates,
    ]
  );

  const onQuestionTypeChanged = useCallback(
    (
      fields: FieldDescriptors<AuthoringFields>,
      newType: QuestionTypeSelectionMenuTypes,
      templateId?: string
    ) => {
      if (!isMeetingSidePanel) {
        if (
          (fields.type.value !== "quiz" && newType === "quiz") ||
          (fields.type.value === "quiz" && newType !== "quiz")
        ) {
          const firstQuestion = fields.questions.value[0] as NominalQuestion;
          const isOnlyOneQuestion = fields.questions.value.length === 1;
          const isQuestionLabelEmpty = !firstQuestion.label;
          const onlyDefaultChoices =
            firstQuestion?.choices && firstQuestion.choices.every(choice => !choice.value);
          const isClean = isOnlyOneQuestion && isQuestionLabelEmpty && onlyDefaultChoices;
          if (!isClean) {
            // Show the type change dialog if the first question has not been edited
            setShowTypeChangeDialog(true);
            setNewQuestionType({ type: newType, templateId: templateId });
          } else {
            convertFormType({ type: newType, templateId });
          }
          return;
        }
      }

      let template: QuestionTemplateDTO | undefined;
      if (templateId) {
        template = questionTemplates.find(t => t.id === templateId);
      }

      if (questionValue.questionType === "nominal") {
        const nominalQuestion = questionValue as NominalQuestion;
        if (
          nominalQuestionChoices.some(item => {
            return item.questionId === questionValue.id;
          })
        ) {
          let updatedChoices = nominalQuestionChoices.map(item => {
            if (item.questionId === questionValue.id) {
              return { ...item, choices: nominalQuestion.choices };
            }
            return item;
          });
          setNominalQuestionChoices(updatedChoices);
        } else {
          const currentChoices: NominalQuestionWithChoices = {
            questionId: questionValue.id,
            choices: nominalQuestion.choices,
          };
          setNominalQuestionChoices(choices => [...choices, currentChoices]);
        }
      }

      // Save questions settings in case user comes back to this question type
      questionSettings[questionValue.id] = {
        ...questionSettings[questionValue.id],
        ...(questionValue.questionType === "nominal" && {
          allowMultipleChoices: !!(questionValue as NominalQuestion).allowMultipleChoices,
          allowUserAddOptions: !!(questionValue as NominalQuestion).allowUserAddOptions,
        }),
        ...(questionValue.questionType !== "open-ended" && {
          allowComments: !!questionValue.allowComments,
          commentAppeal: questionValue.commentAppeal,
        }),
      };

      if (
        questionValue &&
        (questionValue.questionType !== newType ||
          (questionValue.templateId && questionValue.templateId !== templateId))
      ) {
        const label = questionValue.label;
        const id = questionValue.id;
        let newq: Question<QuestionType>;
        switch (newType) {
          case "open-ended":
            newq = createOpenEndedQuestion({ required: questionValue?.required }, id);
            break;
          case "nominal":
            newq = createNominalQuestion(
              {
                required: questionValue?.required,
                allowUserAddOptions: !!questionSettings[questionValue.id]?.allowUserAddOptions,
                allowMultipleChoices: !!questionSettings[questionValue.id]?.allowMultipleChoices,
                allowComments: !!questionSettings[questionValue.id]?.allowComments,
                commentAppeal: questionSettings[questionValue.id]?.commentAppeal,
              },
              id,
              template,
              fields.type.value === "quiz",
              nominalQuestionChoices.find(x => x.questionId === id)?.choices
            );
            break;
          case "ordinal":
            newq = createOrdinalQuestion(
              {
                required: questionValue?.required,
                allowComments: !!questionSettings[questionValue.id]?.allowComments,
                commentAppeal: questionSettings[questionValue.id]?.commentAppeal,
              },
              id,
              template
            );
            break;
          default:
            return;
        }
        newq.label = label;

        const questionIndex = fields.questions.value.findIndex(
          q => q.id === questionField.id.value
        );
        fields.questions.value[questionIndex] = newq;
        fields.questions.onChange([...fields.questions.value]);
      }
    },
    [
      convertFormType,
      nominalQuestionChoices,
      questionField.id.value,
      questionValue,
      questionSettings,
      questionTemplates,
      isMeetingSidePanel,
    ]
  );

  const canDrag = useCallback(
    () => !isLimitedEdit && fields.questions.value.length > 1 && !!collapsed,
    [isLimitedEdit, fields.questions.value.length, collapsed]
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "question-card",
      canDrag,
      item: {
        index,
      },
      end: () => {
        document.body.style.setProperty("cursor", "auto");
      },

      collect: monitor => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [index, canDrag]
  );

  const [, drop] = useDrop({
    accept: "question-card",
    hover: (item, monitor) => {
      // source: https://github.com/rmbh4211995/react-dnd-tutorial/blob/master/client/src/components/Item.jsx
      const draggedItem = item as { index: number };
      const currentRef = ref.current as unknown as { getBoundingClientRect: Function };

      if (!ref.current) {
        return;
      }

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoveredRect = currentRef.getBoundingClientRect();
      const hoverMiddleY = (hoveredRect.bottom - hoveredRect.top) / 2;
      const mousePosition = monitor.getClientOffset()!;
      const hoverClientY = mousePosition.y - hoveredRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      draggedItem.index = hoverIndex;

      const newQuestionsArray = swapArrayLoc([...fields.questions.value], dragIndex, hoverIndex);
      fields.questions.onChange(newQuestionsArray);
    },
  });

  drag(drop(ref));

  return (
    <div ref={ref} style={{ opacity: isDragging ? 0.3 : 1 }}>
      {collapsed !== true ? (
        <Segment styles={showHighLight ? { border: "1px solid var(--polly-accent-color);" } : {}}>
          <Flex column gap="gap.medium">
            <Flex gap="gap.small" vAlign="center">
              {fields.questions.value.length > 1 && (
                <Text content={`Q${index + 1}:`} className="question-title" />
              )}

              <Flex.Item grow>
                <div>
                  {!isMeetingSidePanel ? (
                    <PollyQuestionTypeMenu
                      activeType={
                        fields.type.value === "quiz" ? "quiz" : questionField.questionType.value
                      }
                      activeTemplate={questionField.templateId?.value}
                      templates={questionTemplates}
                      compressed={compressed}
                      allowedTypes={
                        !isEdit
                          ? ["nominal", "open-ended", "ordinal", "quiz"]
                          : fields.type.value === "quiz"
                          ? ["quiz"]
                          : ["nominal", "ordinal", "open-ended"]
                      }
                      onTypeSelected={(type, templateId) =>
                        onQuestionTypeChanged(fields, type, templateId)
                      }
                      disableMenu={isLimitedEdit}
                    />
                  ) : (
                    <PollyQuestionTypeMenuInMeeting
                      activeType={questionField.questionType.value}
                      activeTemplate={questionField.templateId?.value}
                      templates={questionTemplates}
                      compressed={compressed}
                      allowedTypes={
                        fields.type.value === "quiz"
                          ? ["nominal"]
                          : ["nominal", "open-ended", "ordinal"]
                      }
                      onTypeSelected={(type, templateId) =>
                        onQuestionTypeChanged(fields, type, templateId)
                      }
                      disableMenu={isLimitedEdit}
                    />
                  )}
                </div>
              </Flex.Item>

              <Flex.Item push>
                <QuestionCardMenu
                  compressed={compressed}
                  isLimitedEdit={isLimitedEdit}
                  fields={fields}
                  question={questionField}
                  questionTemplates={questionTemplates}
                  addQuestion={addQuestion}
                />
              </Flex.Item>
            </Flex>

            <PollyQuestionEdit
              field={questionField}
              templates={questionTemplates.filter(t => t.type === questionField.questionType.value)}
              questionSettings={{
                enableQuestionComments: questionField.questionType.value !== "open-ended",
                allowMultipleVotes: true,
                audienceCanAddChoice:
                  fields.questions.value.length === 1 && fields.type.value !== "quiz",
                allowCorrectChoice: fields.type.value === "quiz",
              }}
              onTemplateSelected={template => {
                const newQ = createOrdinalQuestion(
                  {
                    templateId: template.id,
                    label: questionField.label.value,
                  },
                  questionField.id.value,
                  template
                );
                const index = fields.questions.value.findIndex(
                  q => q.id === questionField.id.value
                );
                fields.questions.value[index] = newQ;
                fields.questions.onChange([...fields.questions.value]);
              }}
              originalChoices={originalQuestionChoices}
              isLimitedEdit={isLimitedEdit}
              originalAllowedMultipleChoices={
                !!(originalQuestion as NominalQuestion)?.allowMultipleChoices
              }
              setCollapsed={collapsed => {
                setCollapsedForQuestion(questionField.id.value, collapsed, fields);
              }}
            />
          </Flex>
        </Segment>
      ) : (
        <div
          onMouseEnter={() => {
            if (isDragging) {
              return;
            }
            document.body.style.setProperty("cursor", "grab");
          }}
          onMouseLeave={() => {
            if (isDragging) {
              return;
            }
            document.body.style.setProperty("cursor", "auto");
          }}
          onDragEnter={() => {
            document.body.style.setProperty("cursor", "grabbing", "important");
          }}
        >
          <CollapsedQuestion
            compressed={compressed}
            isLimitedEdit={isLimitedEdit}
            fields={fields}
            questionField={questionField}
            questionTemplates={questionTemplates}
            questionValue={questionValue}
            questionIndex={index}
            addQuestion={addQuestion}
            setIsExpanded={expanded => {
              setCollapsedForQuestion(questionField.id.value, !expanded, fields);
            }}
          />
        </div>
      )}
      <PollyDialog
        open={showTypeChangeDialog}
        onCancel={() => {
          setShowTypeChangeDialog(false);
        }}
        header={
          <Flex styles={{ marginLeft: "1rem", marginTop: "1rem" }}>
            <img src={CoreLogo} height={40} alt="Polly" />
            <Flex column className="edit-dialog-header__heading">
              <Text weight="bold" size="medium">
                Polly
              </Text>
            </Flex>
          </Flex>
        }
      >
        <ChangePollyTypeDialog
          fields={fields}
          hideModal={() => {
            setShowTypeChangeDialog(false);
          }}
          newType={newQuestionType!}
          convertFormType={convertFormType}
        />
      </PollyDialog>
    </div>
  );
};

const ChangePollyTypeDialog: React.FC<{
  hideModal: () => void;
  fields: FieldDescriptors<AuthoringFields>;
  newType: { type: QuestionTypeSelectionMenuTypes; templateId?: string };
  convertFormType: (newType: { type: QuestionTypeSelectionMenuTypes; templateId?: string }) => void;
}> = ({ hideModal, fields, newType, convertFormType }) => {
  const [initialPrompt, secondaryPrompt, boldPrompt] = useMemo(() => {
    const type = fields.type.value;
    let initialPrompt = "Quiz questions can only be created with other quiz questions.";
    if (type !== "quiz") {
      initialPrompt = initialPrompt.concat(
        " You can’t mix quiz questions with other question types."
      );
    }
    const secondaryPrompt = type === "quiz" ? "To use other quesiton types" : "To continue";
    const boldPromot =
      type === "quiz"
        ? " convert all other previous quiz questions created to multi-choice questions"
        : " convert all previous questions created to quiz questions";
    return [initialPrompt, secondaryPrompt, boldPromot];
  }, [fields.type.value]);

  return (
    <Flex column>
      <Alert
        warning
        content={
          <>
            <Text content="ⓘ " weight="bold" />
            <Text content={initialPrompt} />
            <br />
            <br />
            <Text content={secondaryPrompt} />
            <Text content={boldPrompt} weight="bold" />
            {fields.type.value !== "quiz" && fields.resultsVisibility.value === "real-time" && (
              <>
                <br />
                <Text content="The audience will no longer see results in real-time" size="small" />
              </>
            )}
          </>
        }
        styles={{ padding: "1rem", margin: "1rem" }}
      />
      <Flex style={{ margin: "0rem 1rem" }} hAlign="end" gap="gap.small">
        <Button
          content={"Cancel"}
          onClick={() => {
            hideModal();
          }}
        />
        <Button
          primary
          content={`Convert to ${fields.type.value === "quiz" ? "Multi-choice" : "Quiz"}`}
          onClick={() => {
            convertFormType(newType);
            hideModal();
          }}
        />
      </Flex>
    </Flex>
  );
};

function swapArrayLoc<T>(arr: T[], from: number, to: number) {
  let temp = arr[to];
  arr[to] = arr[from];
  arr[from] = temp;
  return arr;
}
